import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, SafeAreaView, ScrollView, TouchableOpacity, Alert } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import AvisoVistaPrevia from '../components/AvisoVistaPrevia';
import ScreenBackground from '../components/ScreenBackground';
import { useThemeColors } from '../constants/theme';
import { useRouter } from 'expo-router';
import MapView from '../components/Map/MapView';
import { firestoreService } from '../services/firestoreService';
import { useAppStore } from '../store/useAppStore';
import { db } from '../services/firebaseConfig';
import { doc, onSnapshot } from 'firebase/firestore';
import { useGuardiaEscolar } from '../hooks/useGuardiaEscolar';

const INITIAL_SCHOOL_ROUTES: any[] = [];

export default function SchoolDirectorDashboardScreen() {
  const colors = useThemeColors();
  const router = useRouter();
  const [drivers, setDrivers] = useState<any[]>([]);
  const [routes] = useState(INITIAL_SCHOOL_ROUTES);

  // FIX AUDIT #3: el schoolId DEBE ser el UID real del director. Antes caía
  // a 'demo-school' si no había sesión → todos los directores sin login
  // veían/editaban los MISMOS estudiantes y choferes (visibilidad cruzada).
  const directorUid = useAppStore((state) => state.userFirebaseUid);
  const schoolId = directorUid;

  // FIX AUDIT — Nombre real de la escuela. Antes el header decía "Portal
  // del Colegio Loyola" hardcoded para TODOS los directores, sin importar
  // qué colegio dirigieran. Ahora leemos `schoolName` de /users/{uid}
  // (el campo se setea al registrar el colegio en kyc-director). Si todavía
  // no está, fallback genérico "Portal del Colegio".
  const [schoolName, setSchoolName] = useState<string>('');
  useEffect(() => {
    if (!directorUid) return;
    const ref = doc(db, 'users', directorUid);
    const unsub = onSnapshot(ref, (snap) => {
      const data: any = snap.exists() ? snap.data() : null;
      setSchoolName(String(data?.schoolName ?? data?.school_name ?? '').trim());
    });
    return () => unsub();
  }, [directorUid]);

  React.useEffect(() => {
    if (!schoolId) return; // sin sesión → no abrir listener
    const unsubscribe = firestoreService.listenToSchoolDrivers(schoolId, (fetchedDrivers) => {
      setDrivers(fetchedDrivers);
    });
    return () => unsubscribe();
  }, [schoolId]);

  const handleApproveDriver = async (id: string, name: string) => {
    try {
      await firestoreService.certifySchoolDriver(id);
      Alert.alert(
        'Chofer Certificado',
        `El chofer ${name} ha sido autorizado y certificado formalmente por ${schoolName || 'el colegio'} para operar transporte escolar.`,
        [{ text: 'Entendido' }]
      );
    } catch (e) {
      console.error(e);
      Alert.alert('Error', 'No se pudo certificar al chofer.');
    }
  };

  const handleRevokeDriver = (id: string, name: string) => {
    Alert.alert(
      'Revocar Certificación',
      `¿Estás seguro que deseas revocar la autorización escolar del chofer ${name}?`,
      [
        { text: 'Cancelar', style: 'cancel' },
        {
          text: 'Revocar',
          style: 'destructive',
          onPress: async () => {
            try {
              await firestoreService.revokeSchoolDriver(id);
              Alert.alert('Certificación Revocada', `El chofer ${name} ha sido removido del sistema del colegio.`);
            } catch (e) {
              console.error(e);
              Alert.alert('Error', 'No se pudo revocar la certificación.');
            }
          }
        }
      ]
    );
  };

  const activeDriversCount = drivers.filter(d => d.status === 'Certificado').length;

  // Vertical escolar oculto: ver VERTICAL_ESCOLAR_VISIBLE en featureFlags.ts
  const escolarOculto = useGuardiaEscolar();
  if (escolarOculto) return null;

  return (
    <ScreenBackground>
      <SafeAreaView style={styles.safeArea}>
        <View style={[styles.header, { borderBottomColor: colors.border }]}>
          {router.canGoBack() ? (
            <TouchableOpacity onPress={() => router.back()} style={styles.backButton}>
              <Ionicons name="arrow-back" size={24} color={colors.text} />
            </TouchableOpacity>
          ) : (
            <View style={styles.backButton} />
          )}
          <Text style={[styles.headerTitle, { color: colors.text }]} numberOfLines={1}>
            {schoolName ? `Portal del ${schoolName}` : 'Portal del Colegio'}
          </Text>
          <TouchableOpacity onPress={() => router.push('/school-driver-directory')}>
            <Ionicons name="search" size={24} color={colors.primary} />
          </TouchableOpacity>
        </View>

        <ScrollView contentContainerStyle={styles.container} showsVerticalScrollIndicator={false}>
          <AvisoVistaPrevia que="el panel de una escuela" />
          <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
            Panel Institucional de Gestión y Control del Transporte Escolar
          </Text>

          {/* METRICAS KPI */}
          <View style={styles.kpiContainer}>
            <View style={[styles.kpiCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
              <Ionicons name="people" size={26} color={colors.primary} />
              <Text style={[styles.kpiValue, { color: colors.text }]}>0</Text>
              <Text style={[styles.kpiLabel, { color: colors.textSecondary }]}>Estudiantes en Ruta</Text>
            </View>

            <View style={[styles.kpiCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
              <Ionicons name="shield-checkmark" size={26} color="#4CAF50" />
              <Text style={[styles.kpiValue, { color: colors.text }]}>{activeDriversCount}</Text>
              <Text style={[styles.kpiLabel, { color: colors.textSecondary }]}>Choferes Certificados</Text>
            </View>
          </View>

          <View style={styles.kpiContainer}>
            <View style={[styles.kpiCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
              <Ionicons name="git-branch" size={26} color={colors.accent} />
              <Text style={[styles.kpiValue, { color: colors.text }]}>0</Text>
              <Text style={[styles.kpiLabel, { color: colors.textSecondary }]}>Rutas Operativas</Text>
            </View>

            <View style={[styles.kpiCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
              <Ionicons name="warning" size={26} color="#FF9800" />
              <Text style={[styles.kpiValue, { color: colors.text }]}>0</Text>
              <Text style={[styles.kpiLabel, { color: colors.textSecondary }]}>Alertas SOS Activas</Text>
            </View>
          </View>
          
          <TouchableOpacity 
            style={[styles.directoryBtn, { backgroundColor: colors.primary }]}
            onPress={() => router.push('/school-driver-directory')}
          >
            <Ionicons name="people" size={20} color="#FFF" />
            <Text style={styles.directoryBtnText}>Ver Directorio de Choferes</Text>
          </TouchableOpacity>

          {/* MONITOREO DE RUTAS EN VIVO */}
          <Text style={[styles.sectionTitle, { color: colors.textSecondary }]}>RUTAS EN EL MAPA</Text>
          <View style={[styles.mapContainer, { borderColor: colors.border }]}>
            <MapView 
              style={styles.map}
              initialRegion={{
                latitude: 18.4861,
                longitude: -69.9312,
                latitudeDelta: 0.04,
                longitudeDelta: 0.04
              }}
            />
            <View style={styles.mapLabelOverlay}>
              <Ionicons name="radio" size={16} color="#FF3B30" style={{ marginRight: 6 }} />
              <Text style={styles.mapLabelText}>GPS activo (con la app abierta)</Text>
            </View>
          </View>

          {/* DETALLE DE RUTAS ACTIVAS */}
          {routes.map(route => (
            <View key={route.id} style={[styles.routeCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
              <View style={styles.rowBetween}>
                <View>
                  <Text style={[styles.routeCode, { color: colors.text }]}>{route.code}</Text>
                  <Text style={[styles.routeSub, { color: colors.textSecondary }]}>Chofer: {route.driver} • {route.studentsCount} Alumnos</Text>
                </View>
                <View style={[styles.statusBadge, { backgroundColor: route.status === 'En Ruta' ? 'rgba(76, 175, 80, 0.12)' : 'rgba(33, 150, 243, 0.12)' }]}>
                  <Text style={[styles.statusText, { color: route.status === 'En Ruta' ? '#4CAF50' : '#2196F3' }]}>{route.status}</Text>
                </View>
              </View>
            </View>
          ))}

          {/* CONTROL DE CONDUCTORES POSTULANTES */}
          <Text style={[styles.sectionTitle, { color: colors.textSecondary, marginTop: 25 }]}>
            APROBACIÓN DE CHOFERES POSTULANTES
          </Text>

          {drivers.map(driver => (
            <View key={driver.id} style={[styles.driverCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
              <View style={styles.rowBetween}>
                <View style={{ flex: 1 }}>
                  <Text style={[styles.driverName, { color: colors.text }]}>{driver.name}</Text>
                  <Text style={[styles.driverDesc, { color: colors.textSecondary }]}>
                    {driver.vehicle} • Placa: {driver.plate}
                  </Text>
                  <Text style={[styles.driverDesc, { color: colors.textSecondary, marginTop: 2 }]}>
                    Capacidad: {driver.seats}
                  </Text>
                </View>
                <View style={[styles.statusBadge, { backgroundColor: driver.status === 'Certificado' ? 'rgba(76, 175, 80, 0.12)' : 'rgba(255, 152, 0, 0.12)' }]}>
                  <Text style={[styles.statusText, { color: driver.status === 'Certificado' ? '#4CAF50' : '#FF9800' }]}>
                    {driver.status}
                  </Text>
                </View>
              </View>

              {/* Checklist de validación de seguridad de la directora.
                  Decía "VERIFICADOS POR IA" y eso era falso: no existe ninguna
                  IA en el repositorio (ni Vertex, ni Gemini, ni OCR, ni una
                  dependencia). Quien aprueba es un admin a mano desde
                  `reviewKycSubmission` (functions/src/kyc.ts), y la Política de
                  Privacidad punto 2.c ahora dice exactamente eso. Una pantalla
                  que promete IA y una política que la niega es la contradicción
                  que un revisor de Play cita sin esforzarse. */}
              <View style={[styles.checklistCard, { backgroundColor: 'transparent', borderColor: colors.border }]}>
                <Text style={[styles.checklistTitle, { color: colors.textSecondary }]}>
                  REQUISITOS DE ALTA SEGURIDAD, VERIFICADOS POR GO SAFELY:
                </Text>
                <View style={styles.checkRow}>
                  <Ionicons name="checkmark-circle" size={16} color="#4CAF50" />
                  <Text style={[styles.checkText, { color: colors.text }]}>Cédula y Licencia de conducir dominicanas vigentes</Text>
                </View>
                <View style={styles.checkRow}>
                  <Ionicons name="checkmark-circle" size={16} color="#4CAF50" />
                  <Text style={[styles.checkText, { color: colors.text }]}>Papel de Buena Conducta verificado y aprobado</Text>
                </View>
                <View style={styles.checkRow}>
                  <Ionicons name="checkmark-circle" size={16} color="#4CAF50" />
                  <Text style={[styles.checkText, { color: colors.text }]}>Cinturones de seguridad funcionales traseros verificados por foto en vivo</Text>
                </View>
                <View style={styles.checkRow}>
                  <Ionicons name="checkmark-circle" size={16} color="#4CAF50" />
                  <Text style={[styles.checkText, { color: colors.text }]}>Indicador de Seguro de Niños (Child Lock) activo verificado</Text>
                </View>
              </View>

              {driver.status === 'Pendiente Aprobación' ? (
                <View style={styles.actionsRow}>
                  <TouchableOpacity 
                    style={[styles.approveBtn, { backgroundColor: colors.accent }]}
                    onPress={() => handleApproveDriver(driver.id, driver.name)}
                  >
                    <Text style={styles.approveBtnText}>Aprobar Licencia Escolar</Text>
                  </TouchableOpacity>
                  <TouchableOpacity 
                    style={styles.rejectBtn}
                    onPress={() => handleRevokeDriver(driver.id, driver.name)}
                  >
                    <Text style={styles.rejectBtnText}>Rechazar</Text>
                  </TouchableOpacity>
                </View>
              ) : (
                <TouchableOpacity 
                  style={[styles.revokeBtn, { borderColor: colors.border }]}
                  onPress={() => handleRevokeDriver(driver.id, driver.name)}
                >
                  <Text style={styles.revokeBtnText}>Revocar Certificación Escolar</Text>
                </TouchableOpacity>
              )}
            </View>
          ))}

          <View style={{ height: 100 }} />
        </ScrollView>
      </SafeAreaView>
    </ScreenBackground>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 15,
    borderBottomWidth: 1,
    backgroundColor: 'transparent',
  },
  backButton: {
    padding: 8,
    marginLeft: -8,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  container: {
    padding: 20,
    paddingTop: 40,
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
    flexGrow: 1,
  },
  subtitle: {
    fontSize: 14,
    marginBottom: 20,
    lineHeight: 20,
  },
  kpiContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 12,
  },
  kpiCard: {
    flex: 1,
    borderRadius: 16,
    padding: 16,
    borderWidth: 1,
    alignItems: 'flex-start',
  },
  kpiValue: {
    fontSize: 22,
    fontWeight: 'bold',
    marginVertical: 4,
  },
  kpiLabel: {
    fontSize: 11,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 15,
    marginLeft: 4,
    marginTop: 25,
    letterSpacing: 1,
  },
  directoryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    borderRadius: 12,
    marginBottom: 20,
    marginTop: 5,
  },
  directoryBtnText: {
    color: '#FFF',
    fontWeight: 'bold',
    marginLeft: 8,
    fontSize: 14,
  },

  mapContainer: {
    height: 180,
    borderRadius: 20,
    overflow: 'hidden',
    borderWidth: 1,
    position: 'relative',
    marginBottom: 16,
  },
  map: {
    flex: 1,
  },
  mapLabelOverlay: {
    position: 'absolute',
    top: 12,
    left: 12,
    backgroundColor: 'rgba(0,0,0,0.7)',
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 8,
  },
  mapLabelText: {
    color: '#FFF',
    fontSize: 11,
    fontWeight: 'bold',
  },
  routeCard: {
    padding: 16,
    borderRadius: 16,
    borderWidth: 1,
    marginBottom: 12,
  },
  rowBetween: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  routeCode: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  routeSub: {
    fontSize: 13,
    marginTop: 2,
  },
  statusBadge: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 8,
  },
  statusText: {
    fontSize: 11,
    fontWeight: 'bold',
  },
  driverCard: {
    padding: 20,
    borderRadius: 20,
    borderWidth: 1,
    marginBottom: 16,
  },
  driverName: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  driverDesc: {
    fontSize: 13,
    marginTop: 2,
  },
  checklistCard: {
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
    marginTop: 15,
    marginBottom: 15,
    gap: 8,
  },
  checklistTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  checkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  checkText: {
    fontSize: 11,
    lineHeight: 15,
  },
  actionsRow: {
    flexDirection: 'row',
    gap: 12,
  },
  approveBtn: {
    flex: 2,
    height: 44,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  approveBtnText: {
    color: '#FFF',
    fontWeight: 'bold',
    fontSize: 14,
  },
  rejectBtn: {
    flex: 1,
    height: 44,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#FF3B30',
    backgroundColor: 'rgba(255, 59, 48, 0.05)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  rejectBtnText: {
    color: '#FF3B30',
    fontWeight: 'bold',
    fontSize: 14,
  },
  revokeBtn: {
    height: 44,
    borderRadius: 10,
    borderWidth: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  revokeBtnText: {
    color: '#FF3B30',
    fontWeight: 'bold',
    fontSize: 14,
  }
});
